import type { MouseEvent } from 'react';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router';
import { PageId } from '../types';
import './Sidebar.scss';
import logo from '../assets/keylexical-long.svg';
import Avatar from './Avatar';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import { userAtom, userProfileModalOpenAtom, userLoginModalOpenAtom } from '../atoms/userAtom';
import { compactModeAtom } from '../atoms/uiAtoms';

type SidebarProps = {
  pageId?: PageId;
};

const Sidebar = ({ pageId }: SidebarProps) => {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useAtom(compactModeAtom);
  const user = useAtomValue(userAtom);
  const setUserProfileModalOpen = useSetAtom(userProfileModalOpenAtom);
  const setUserLoginModalOpen = useSetAtom(userLoginModalOpenAtom);

  const navItems = useMemo(() => ([
    { to: '/', label: 'Home', mobileLabel: 'Home', id: 'home', icon: 'ic-home' },
    { to: `/${PageId.HISTORY}`, label: 'History', mobileLabel: 'History', id: PageId.HISTORY, icon: 'ic-history' },
    { to: `/${PageId.SERIES}`, label: 'TV Shows', mobileLabel: 'TV', id: PageId.SERIES, icon: 'ic-tv' },
    { to: `/${PageId.MOVIES}`, label: 'Movies', mobileLabel: 'Movies', id: PageId.MOVIES, icon: 'ic-movie' },
    { to: `/${PageId.YOUTUBE}`, label: 'YouTube', mobileLabel: 'YouTube', id: PageId.YOUTUBE, icon: 'ic-movie', customIcon: 'youtube' as const },
    { to: `/${PageId.COLLECTION}`, label: 'Collection', mobileLabel: 'More', id: PageId.COLLECTION, icon: 'ic-three-dots-vertical' },
  ]), []);

  const isActive = (to: string, id?: string) => {
    if (id && pageId && id === pageId) {
      return true;
    }
    if (to === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(to);
  };

  const toggleSidebar = () => {
    setIsCollapsed((v) => !v);
  };

  const handleAvatarClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (user) {
      setUserProfileModalOpen(true);
    } else {
      setUserLoginModalOpen(true);
    }
  };

  return (
    <nav className={`nav-sidebar ${isCollapsed ? 'close' : ''} bg-body border-end`}>
      <ul className="list-unstyled m-0">
        <li className="controls">
          <button className={`btn btn-clear btn-sm toggle-btn`} onClick={toggleSidebar} aria-label="Toggle sidebar">
            <span className="ic ic-layout-sidebar" />
          </button>
        </li>
        <li className="logo-wrapper">
          <Link to="/" className="logo">
            <img src={logo} alt="KeyLexical" />
          </Link>
        </li>

        {navItems.map(({ to, label, mobileLabel, id, icon, customIcon }) => (
          <li key={to} className={isActive(to, id) ? 'active' : undefined}>
            <Link
              to={to}
              title={label}
              state={id === PageId.SERIES || id === PageId.MOVIES || id === PageId.YOUTUBE ? { resetSearch: true } : undefined}
            >
              {customIcon === 'youtube' ? (
                <svg className="youtube-nav-icon" viewBox="0 0 90 64" aria-hidden="true" focusable="false">
                  <rect x="3" y="3" width="84" height="58" rx="14" fill="#FF0000" />
                  <polygon points="38,20 38,44 58,32" fill="#FFFFFF" />
                </svg>
              ) : (
                <span className={`ic ${icon}`} />
              )}
              <span className="label label-desktop">{label}</span>
              <span className="label label-mobile">{mobileLabel ?? label}</span>
            </Link>
          </li>
        ))}

        <li className="spacer"/>
        <li className="d-none d-lg-block profile">
          <Link to="#" onClick={handleAvatarClick} title="Profile">
            <Avatar src={user?.avatar} />
            <span className="label">Profile</span>
          </Link>
        </li>
      </ul>
    </nav>
  );
};

export default Sidebar;
